import { Fragment, useEffect, useRef, useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { BOOKS_ICONS, BOOKS_NAV, booksPath } from '../pages/books/nav';
import '../pages/books/books.css';
import { useAuth } from '../contexts/AuthContext';
import { SwitcherMenu } from '../shared/SwitcherMenu';
import { flattenMemberships } from '../shared/memberships';

type NavItem = { to: string; label: string; group?: string };

// Eight plain words. Studios / To-Do / Accounting / Curriculum Review stay
// reachable by URL but are off the menu (2026-09 Leah pass).
const NAV: NavItem[] = [
  { to: '/', label: 'Home' },
  { to: '/people', label: 'People' },
  { to: '/money', label: 'Money' },
  { to: '/games', label: 'Games' },
  { to: '/lessons', label: 'Lessons' },
  { to: '/illustrations', label: 'Illustrations' },
  { to: '/books', label: 'Books' },
  { to: '/help', label: 'Help' },
];

// The Marketing group sits between Books and Help (2026-10-26 autopilot).
const MARKETING_NAV: NavItem[] = [
  { to: '/marketing', label: 'Overview' },
  { to: '/marketing/queue', label: 'Queue' },
  { to: '/marketing/inbox', label: 'Inbox' },
  { to: '/marketing/settings', label: 'Settings' },
];

// The outside accountant (a books_access row, no admin row) sees two words.
const BOOKS_ONLY_NAV: NavItem[] = NAV.filter((n) => n.to === '/books' || n.to === '/help');

export function AppShell() {
  const { user, signOut, memberships, booksOnly } = useAuth();
  const nav = booksOnly ? BOOKS_ONLY_NAV : NAV;
  const loc = useLocation();
  // Inside /books the sidebar becomes the Books menu, like an accounting app.
  const inBooks = loc.pathname === '/books' || loc.pathname.startsWith('/books/');
  const [railNarrow, setRailNarrow] = useState<boolean>(() => {
    try { return localStorage.getItem('books.rail.narrow') === '1'; } catch { return false; }
  });
  const toggleRail = () => setRailNarrow((v) => {
    try { localStorage.setItem('books.rail.narrow', v ? '0' : '1'); } catch { /* private window */ }
    return !v;
  });
  const narrow = inBooks && railNarrow;
  const inMarketing = loc.pathname === '/marketing' || loc.pathname.startsWith('/marketing/');
  // Phone/narrow: the sidebar becomes an off-canvas drawer behind the top bar.
  const [menuOpen, setMenuOpen] = useState(false);
  const menuBtnRef = useRef<HTMLButtonElement>(null);
  const navRef = useRef<HTMLElement>(null);

  // While the drawer is open: Escape closes it and focus moves to the first
  // nav link; on close, focus returns to the hamburger that opened it.
  useEffect(() => {
    if (!menuOpen) return;
    navRef.current?.querySelector<HTMLAnchorElement>('a')?.focus();
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      menuBtnRef.current?.focus();
    };
  }, [menuOpen]);
  const initials =
    (user?.email ?? '?')
      .split('@')[0]
      .split(/[._-]/)
      .filter(Boolean)
      .slice(0, 2)
      .map((s) => s[0]?.toUpperCase() ?? '')
      .join('') || '?';

  // Every hat this account holds. With 2+ (e.g. admin + a client record) the
  // foot becomes the experience switcher; a single-membership admin keeps the
  // plain account line. A books-only account has no admin hat, so the switcher
  // would mislabel it; it always gets the plain account line.
  const hats = memberships ? flattenMemberships(memberships) : [];
  const showSwitcher = !booksOnly && !inBooks && hats.length >= 2;

  return (
    <div className={`shell${inBooks ? ' shell--books' : ''}${narrow ? ' shell--narrow' : ''}`}>
      <header className="topbar">
        <span className="brand-mark">Backbar</span>
        <span className="brand-sub">Rootegra</span>
        <button
          ref={menuBtnRef}
          type="button"
          className="topbar-menu"
          aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={menuOpen}
          aria-controls="sidebar-nav"
          onClick={() => setMenuOpen((v) => !v)}
        >
          <i /><i /><i />
        </button>
      </header>
      <button
        type="button"
        className={menuOpen ? 'scrim show' : 'scrim'}
        aria-label="Close menu"
        tabIndex={menuOpen ? 0 : -1}
        onClick={() => setMenuOpen(false)}
      />
      <aside className={menuOpen ? 'sidebar open' : 'sidebar'}>
        <div className="brand">
          <span className="brand-mark">{inBooks ? (narrow ? 'B' : 'Books') : 'Backbar'}</span>
          {!narrow && <span className="brand-sub">{inBooks ? 'Rootegra LLC' : 'Rootegra'}</span>}
        </div>
        {inBooks ? (
          <nav id="sidebar-nav" ref={navRef} className="nav bk-nav" aria-label="Books" onClick={() => setMenuOpen(false)}>
            <button type="button" className="bk-rail-toggle" onClick={(e) => { e.stopPropagation(); toggleRail(); }} aria-label={narrow ? 'Show menu labels' : 'Collapse menu'} title={narrow ? 'Show menu labels' : 'Collapse menu'}>
              <span aria-hidden="true">{narrow ? '»' : '«'}</span>{!narrow && ' Collapse'}
            </button>
            {!booksOnly && (
              <Link to="/" className="bk-nav-back" title="Back to Backbar">
                <span aria-hidden="true" className="bk-nav-ico">‹</span><span className="bk-nav-label">Back to Backbar</span>
              </Link>
            )}
            {BOOKS_NAV.map((g, gi) => (
              <div key={gi} className="bk-nav-group">
                {g.group && <div className="bk-nav-heading">{g.group}</div>}
                {g.items.map((i) => (
                  <NavLink key={i.key} to={booksPath(i.key)} end title={i.label} className={({ isActive }) => (isActive ? 'on' : '')}>
                    <span className="bk-nav-ico">{BOOKS_ICONS[i.key]}</span>
                    <span className="bk-nav-label">{i.label}</span>
                  </NavLink>
                ))}
              </div>
            ))}
            {booksOnly && (
              <div className="bk-nav-group">
                <NavLink to="/help" title="Help"><span className="bk-nav-ico">?</span><span className="bk-nav-label">Help</span></NavLink>
              </div>
            )}
          </nav>
        ) : (
          <nav id="sidebar-nav" ref={navRef} className="nav" onClick={() => setMenuOpen(false)}>
            {nav.map((n) => (
              <Fragment key={n.to}>
                {n.to === '/help' && !booksOnly && (
                  <>
                    <div className="nav-group">Marketing</div>
                    {MARKETING_NAV.map((m) => (
                      <NavLink key={m.to} to={m.to} end className={({ isActive }) => (isActive ? 'on' : '')}>
                        <span className="nav-dot" />
                        {m.label}
                      </NavLink>
                    ))}
                    <div aria-hidden="true" style={{ borderTop: '1px solid rgba(245,239,230,.10)', margin: '8px 12px 6px' }} />
                  </>
                )}
                <NavLink to={n.to} end={n.to === '/'} className={({ isActive }) => (isActive ? 'on' : '')}>
                  <span className="nav-dot" />
                  {n.label}
                </NavLink>
              </Fragment>
            ))}
          </nav>
        )}
        {showSwitcher ? (
          <div className="sidebar-foot sidebar-foot--switcher">
            <SwitcherMenu
              accountName={initials}
              accountEmail={user?.email}
              memberships={hats}
              currentId="admin"
              onSignOut={() => void signOut()}
            />
          </div>
        ) : (
          <div className="sidebar-foot">
            <span>{initials} · {user?.email}</span>
            <button onClick={() => void signOut()}>Sign out</button>
          </div>
        )}
      </aside>
      <main className={inBooks || inMarketing ? 'main main--books' : 'main'}>
        <Outlet />
      </main>
    </div>
  );
}
